import { AppEnv } from '@brightdrop/app-config';
import { makeThunkCreatorConfig } from '../utils/store/thunk.helper';

export const DEFAULT_API_HEADERS = {
  'Content-Type': 'application/json',
  Accept: 'application/json',
};

export const CONTENT_TYPE_HEADER = 'Content-Type';
export const ACCEPT_LANGUAGE_HEADER = 'Accept-Language';
export const CONTENT_TYPE_VALUE = 'application/json';
export const CONTENT_TYPE_FILE_VALUE = 'multipart/form-data';

export const API_VERSION_DEFAULTS = {
  default: '2021-11-15',
  alertsAPI: '2021-11-15',
  alertsSubscriptionsUpdateAPI: '2022-07-01',
  alertsListAPI: '2023-12-01',
  assetComplianceAPI: '2021-11-15',
  assetDetailsAPI: '2022-05-24',
  assetRecallsAPI: '2024-09-11',
  assetLocationsAPI: '2023-11-07',
  assetHistoryAPI: '2023-12-08',
  fleetAPI: '2024-09-26',
  fleetListAPI: '2024-12-17',
  fleetListUpdateAPI: '2025-01-14',
  coldChainFleetAPI: '2023-05-09',
  orgTypeAPI: '2023-07-17',
  ordersAddEditAPI: '2023-08-15',
  orderDefaultAPI: '2023-05-23',
  batteryReadinessAPI: '2023-08-16',
  assetCountSummaryAPI: '2023-11-22',
  dispatchAPI: '2023-11-16',
  vehicleOrdersAPI: '2024-06-01',
  validateSsoEmailAPI: '2024-07-18',
  vehicleListOverviewAPI: '2024-12-03',
  competitiveAssistanceProgramAPI: '2024-10-08',
  recallSummary: '2024-12-04',
  oilLifeSummaryAPI: '2024-10-09',
  pendingVehiclesListAPI: '2025-02-11',
  peripheralsOnboardingAPI: '2024-10-08',
  peripheralsAPI: '2024-10-23',
  peripheralsUnlinkAPI: '2024-11-06',
  peripheralsMoveAPI: '2024-11-06',
  peripheralsActivateAPI: '2026-02-24',
  recallsAPI: '2024-10-23',
  chargeLevelSummary: '2024-11-06',
  permissionsAPI: '2024-10-07',
  orgListAPI: '2024-11-20',
  remoteCommandsAPI: '2025-01-14',
  pubSubAPI: '2022-05-24',
  profileApiLatest: '2023-10-10',
  batchOnboardAPI: '2025-01-14',
  pendingVerificationVehiclesAPI: '2025-02-11',
  incompleteVehiclesAPI: '2025-02-25',
  enrollAPI: '2025-01-14',
  orgVerificationStatusAPI: '2026-06-02',
  enrollmentLookupAPI: '2026-05-19',
  updateOrgAPI: '2024-12-04',
  inviteAPI: '2024-10-21',
  addUserAPI: '2025-01-28',
  acceptInviteAPI: '2024-11-20',
  assetStatusesAPI: '2025-02-11',
  acceptOrgTermsConditionsAPI: '2025-02-11',
  vehicleListBatteryConnectedOnlyAPI: '2026-01-13',
  userListAPIv2: '2025-02-11',
  assetIgnitionAPI: '2025-02-11',
  advisorVehicleListApi: '2025-02-11',
  rejectConfirmVehicleAPI: '2024-11-11',
  retryOnboardingVehicleAPI: '2025-07-15',
  camerasSettingsUpdateAPI: '2025-03-12',
  camerasSettingsGetAPI: '2025-03-26',
  entitlementAPI: '2025-06-11',
  alertsBatchToggleAPI: '2025-05-20',
  batchVehicleOffboardAPI: '2025-03-11',
  connectivityStatusAPI: '2025-08-26',
  vehicleListEnhancementPhase1API: '2025-09-09',
  vehicleListOverviewConsolidationAPI: '2025-11-04',
  rulesAPI: '2025-10-07',
  vehicleListExportConsolidationAPI: '2025-11-18',
  vehicleListOverviewAiSearchExportAPI: '2026-03-10',
  vehicleListOverviewDiagnosticsAPI: '2025-11-04', // Overview and Diagnostics
  vehicleListDiagnosticsExportAPI: '2026-01-13',
};

export const DEFAULT_API_CONFIG = makeThunkCreatorConfig({
  timeout:
    globalThis.appConfig.env === AppEnv.DEV ||
    globalThis.appConfig.env === AppEnv.QA
      ? 15000
      : 5000,
  retries: 2,
  delay: 100,
  customHeaders: {
    [CONTENT_TYPE_HEADER]: CONTENT_TYPE_VALUE,
  },
});
export const ALERTS_API_CONFIG = makeThunkCreatorConfig({
  timeout: 15000,
  retries: 2,
  delay: 100,
  customHeaders: {
    [CONTENT_TYPE_HEADER]: CONTENT_TYPE_VALUE,
  },
});

export const ASSETS_API_BASE_CONFIG = makeThunkCreatorConfig({
  timeout:
    globalThis.appConfig.env === AppEnv.DEV ||
    globalThis.appConfig.env === AppEnv.QA
      ? 15000
      : 5000,
  retries: 2,
  delay: 100,
  customHeaders: {
    [CONTENT_TYPE_HEADER]: CONTENT_TYPE_VALUE,
  },
});

export const PERIPHERALS_API_CONFIG = makeThunkCreatorConfig({
  timeout:
    globalThis.appConfig.env === AppEnv.DEV ||
    globalThis.appConfig.env === AppEnv.QA
      ? 15000
      : 5000,
  retries: 2,
  delay: 100,
  customHeaders: {
    [CONTENT_TYPE_HEADER]: CONTENT_TYPE_FILE_VALUE,
  },
});
// Extended timeout for remote commands due to potential delays in command execution. Will adjust if needed after UAT Testing
export const REMOTE_COMMAND_API_CONFIG = makeThunkCreatorConfig({
  timeout: 150000,
  retries: 0,
  delay: 100,
  customHeaders: {
    [CONTENT_TYPE_HEADER]: CONTENT_TYPE_VALUE,
  },
});

export const BATCH_VEHICLES_API_BASE_CONFIG = makeThunkCreatorConfig({
  timeout:
    globalThis.appConfig.env === AppEnv.DEV ||
    globalThis.appConfig.env === AppEnv.QA
      ? 15000
      : 5000,
  retries: 2,
  delay: 100,
  customHeaders: {
    [CONTENT_TYPE_HEADER]: CONTENT_TYPE_FILE_VALUE,
  },
});

export const BD_ORGANIZATION_HEADER_NAME = 'x-bd-organization';
export const BD_FLEET_HEADER_NAME = 'x-bd-fleet';
export const CONTINUATION_TOKEN_HEADER_NAME = 'continuationToken';
export const CS_ORGANIZATION_HEADER_NAME = 'cs-organization';
export const CS_CONTINUATION_TOKEN_HEADER_NAME = 'cs-continuation-token';
export const CS_FLEET_HEADER_NAME = 'cs-fleet';
/**
 * Re-exported rather than declared, because the packages this app federates with send the same
 * header and the name has to be one string. `@brightdrop/auth-client` owns it: it holds the value
 * the header carries, and is the only package every base query already shares as a singleton.
 */
export { CS_LOCALE_HEADER_NAME } from '@brightdrop/auth-client';
